/**
 * SubscribedPage — synthetic landing for Google Ads conversion tracking.
 *
 * Reached only via a hard nav from ``BillingChrome.handleSubscribed`` after a
 * paid subscribe path (full checkout success OR one-tap reactivate with card
 * on file). The trial sheet's "Keep playing free" does NOT land here: it starts
 * nothing — see ``UnlockModal``.
 *
 * It sits outside the auth providers and a hard nav drops the in-memory token, so it
 * refreshes first (2026-10-31: every call here went out signed out). With a session it says
 * what the family has, from /billing/state, and asks for the mailing address once.
 *
 * The Google Ads tag fires on mount via ``window.gtag``. Configure the
 * conversion id at build time via ``VITE_GOOGLE_ADS_CONVERSION_ID``
 * (e.g. ``AW-123456789/AbC-DefGhIjKlMnOp``). With no id set, the page still
 * renders fine — useful in dev/preview where Ads isn't loaded.
 */
import { useEffect, useState } from "react";
import { auth, refreshAccessToken } from "../api";
import { ChromeTokens } from "../chrome/tokens";
import { Icon } from "../chrome/Icon";
import { getBillingState } from "./api";
import { MailingAddressPrompt } from "./MailingAddressPrompt";
import { subscribedLine } from "./subscribedLine";
import type { BillingState } from "./types";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function safeReturnTo(): string {
  const raw = new URLSearchParams(window.location.search).get("return_to");
  // Same-origin path only — no protocol-relative or external URLs.
  if (typeof raw === "string" && raw.startsWith("/") && !raw.startsWith("//")) {
    return raw;
  }
  return "/";
}

export function SubscribedPage(): JSX.Element {
  const returnTo = safeReturnTo();
  const [signedIn, setSignedIn] = useState(false);
  const [billing, setBilling] = useState<BillingState | null>(null);

  useEffect(() => {
    let live = true;
    const session = refreshAccessToken();
    void session.then((ok) => {
      if (!ok || !live) return;
      setSignedIn(true);
      getBillingState().then((s) => { if (live) setBilling(s); }).catch(() => { /* the plain line */ });
    });
    const id = import.meta.env.VITE_GOOGLE_ADS_CONVERSION_ID;
    if (typeof window.gtag === "function" && typeof id === "string" && id) {
      const gtag = window.gtag;
      // The prod validation family never counts as a paid-ads conversion (VALIDATION_ACCOUNTS.md
      // §2). It is comped and should never reach this page; if it ever does, it stays out.
      void session.then(() => auth.me())
        .then((me) => { if (!me.is_test_account) gtag("event", "conversion", { send_to: id }); })
        .catch(() => gtag("event", "conversion", { send_to: id }));
    }
    return () => { live = false; };
  }, []);

  // Replace, not assign: Back to this page and Continue again would remix a second time.
  const handleContinue = () => {
    window.location.replace(returnTo);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: ChromeTokens.color.base,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: ChromeTokens.spacing.xl,
        fontFamily: ChromeTokens.font.ui,
        color: ChromeTokens.color.ink,
      }}
    >
      <div
        style={{
          width: "min(420px, 86vw)",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          gap: ChromeTokens.spacing.lg,
        }}
      >
        <span
          aria-hidden
          style={{
            width: 72,
            height: 72,
            borderRadius: ChromeTokens.radius.pill,
            margin: "0 auto",
            background: `color-mix(in srgb, var(--chrome-color-grass) 32%, var(--chrome-color-base))`,
            display: "grid",
            placeItems: "center",
          }}
        >
          <Icon name="check" size={36} stroke={ChromeTokens.color.clay} />
        </span>

        <h1
          style={{
            fontFamily: ChromeTokens.font.display,
            fontSize: 36,
            lineHeight: 1.05,
            margin: 0,
          }}
        >
          You're all set!
        </h1>

        <p
          style={{
            color: ChromeTokens.color.inkMuted,
            fontSize: 15,
            lineHeight: 1.5,
            margin: 0,
          }}
        >
          {subscribedLine(billing)}
        </p>

        <button
          type="button"
          onClick={handleContinue}
          style={{
            minHeight: 48,
            fontSize: 16,
            fontWeight: 800,
            fontFamily: ChromeTokens.font.ui,
            border: "none",
            borderRadius: ChromeTokens.radius.pill,
            cursor: "pointer",
            background: ChromeTokens.color.sun,
            color: ChromeTokens.color.ink,
            boxShadow: ChromeTokens.shadow.soft,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: ChromeTokens.spacing.sm,
            padding: `0 ${ChromeTokens.spacing.xl}px`,
          }}
        >
          <Icon name="sparkles" size={18} stroke={ChromeTokens.color.ink} />
          Continue
        </button>
      </div>
      {signedIn && <MailingAddressPrompt outsideAuth />}
    </div>
  );
}
